import React, { useCallback, useEffect, useMemo } from 'react'
import { useController } from 'react-hook-form'

import GeoZoneAutocomplete from './GeoZoneAutocomplete'

import type { ZoneOption } from './GeoZoneAutocomplete'
import type { SxProps, Theme } from '@mui/material/styles'
import type { Control, FieldValues, Path } from 'react-hook-form'
import type { ZoneMatch } from 'types/modules/zones/zone'

type GeoZoneRoleFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: string
  label: string
  zones: ZoneMatch[]
  loading?: boolean
  // Whether `zones` answers for the current point; before that, an exclusion may still apply.
  resolved?: boolean
  popperSx?: SxProps<Theme>
}

// Every containing zone is on unless its id is in the field's excluded list; the list is all that is saved.
const GeoZoneRoleField = <TFieldValues extends FieldValues>({
  control,
  name,
  label,
  zones,
  loading = false,
  resolved = false,
  popperSx,
}: GeoZoneRoleFieldProps<TFieldValues>) => {
  const { field } = useController({ control, name: name as Path<TFieldValues> })
  const excludedIds = useMemo(() => (field.value as string[] | undefined) ?? [], [field.value])
  const value = zones.filter(zone => !excludedIds.includes(zone.id))
  const { onChange } = field

  // An exclusion for a zone the pin has left would be saved where no field shows it.
  useEffect(() => {
    if (!resolved) return
    const kept = excludedIds.filter(id => zones.some(zone => zone.id === id))
    if (kept.length !== excludedIds.length) onChange(kept)
  }, [resolved, zones, excludedIds, onChange])

  const handleChange = useCallback(
    (selected: ZoneOption[]) => {
      const selectedIds = selected.map(zone => zone.id)
      field.onChange(zones.filter(zone => !selectedIds.includes(zone.id)).map(zone => zone.id))
    },
    [field, zones]
  )

  return <GeoZoneAutocomplete label={label} loading={loading} options={zones} popperSx={popperSx} value={value} onChange={handleChange} />
}

export default GeoZoneRoleField
